import React from 'react';
import { PRIORITY_AR, STATUS_AR } from '../utils/labels';
import { useNavigate } from 'react-router-dom';
import { TagList } from '../components/TagBadge';
import { fmtDateBoth } from '../utils/dates';

function dateOnly(d) {
  if (!d) return null;
  return String(d).split('T')[0];
}

// بالتوقيت المحلّي لا UTC — toISOString كانت تُرجع يوم أمس بعد الساعة ٩ مساءً
// بتوقيت السعودية، فتقفز مهام اليوم إلى عمود «متأخرة»
const localKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function todayStr() { return localKey(new Date()); }
function tomorrowStr() {
  const d = new Date(); d.setDate(d.getDate() + 1);
  return localKey(d);
}

function TaskCard({ task, onClick }) {
  const overdue = task.due_date && new Date(task.due_date) < new Date() &&
    !['completed', 'cancelled'].includes(task.status);
  return (
    <div
      onClick={onClick}
      className="task-card"
      style={{
        background: 'var(--surface, #fff)',
        border: '1px solid var(--border, var(--border))',
        borderRadius: 8, padding: 12, cursor: 'pointer',
        marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14 }}>{task.title}</div>
      {task.tags?.length > 0 && <TagList tags={task.tags} />}
      {(task.bucket || (task.show_checklist_on_card && task.checklist_items_count > 0)) && (
        <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-muted)' }}>
          {task.bucket && <span>🗂️ {task.bucket}</span>}
          {task.show_checklist_on_card && task.checklist_items_count > 0 && (
            <span title="قائمة الاختيار">☑️ {task.checklist_done_count}/{task.checklist_items_count}</span>
          )}
        </div>
      )}
      {task.show_notes_on_card && task.notes && <div className="card-notes">{task.notes}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
        <span>
          {(task.assignees || []).length > 1
            ? `${task.assignees[0].name} +${task.assignees.length - 1}`
            : (task.assignee?.name || 'غير محدد')}
        </span>
        <span className={`badge badge-${task.priority}`} style={{ fontSize: 10 }}>{PRIORITY_AR[task.priority]}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
        <span className={`badge badge-${task.status}`} style={{ fontSize: 10 }}>{STATUS_AR[task.status]}</span>
        {task.due_date && (
          <span style={{ color: overdue ? 'var(--danger)' : 'var(--text-muted)', fontWeight: overdue ? 600 : 400 }}>
            {fmtDateBoth(task.due_date)}
          </span>
        )}
      </div>
      {task.start_date && dateOnly(task.start_date) > todayStr() && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          ▶ يبدأ: {fmtDateBoth(task.start_date)}
        </div>
      )}
    </div>
  );
}

function Column({ title, tasks, color, emptyText }) {
  const navigate = useNavigate();
  return (
    <div style={{
      flex: 1, minWidth: 240,
      background: 'var(--gray-50, var(--surface-alt))',
      borderRadius: 10, padding: 12,
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ fontSize: 14, margin: 0, color }}>{title}</h3>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface)', padding: '2px 8px', borderRadius: 999 }}>
          {tasks.length}
        </span>
      </div>
      {tasks.length === 0 ? (
        <div style={{ padding: '20px 8px', color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>
          {emptyText}
        </div>
      ) : (
        tasks.map(t => <TaskCard key={t.id} task={t} onClick={() => navigate(`/tasks/${t.id}`)} />)
      )}
    </div>
  );
}

export default function TasksColumns({ tasks }) {
  const today = todayStr();
  const tomorrow = tomorrowStr();

  const isActive = (t) => !['completed', 'cancelled'].includes(t.status);

  const overdue = tasks.filter(t => isActive(t) && t.due_date && dateOnly(t.due_date) < today);
  const todayTasks = tasks.filter(t => isActive(t) && t.due_date && dateOnly(t.due_date) === today);
  const tomorrowTasks = tasks.filter(t => isActive(t) && t.due_date && dateOnly(t.due_date) === tomorrow);

  const noDateCount = tasks.filter(t => isActive(t) && !t.due_date).length;
  const completedShown = tasks.filter(t => t.status === 'completed').length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Column title="🔴 متأخرة" tasks={overdue} color="var(--c-coral)" emptyText="لا توجد مهام متأخرة" />
        <Column title="📅 اليوم" tasks={todayTasks} color="var(--c-blue-500)" emptyText="لا توجد مهام لليوم" />
        <Column title="⏭ غداً" tasks={tomorrowTasks} color="var(--c-success)" emptyText="لا توجد مهام للغد" />
      </div>
      {(noDateCount > 0 || completedShown > 0) && (
        <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 16, justifyContent: 'center' }}>
          {noDateCount > 0 && <span>📌 {noDateCount} مهمة بدون تاريخ تسليم</span>}
          {completedShown > 0 && <span>✅ {completedShown} مهمة منجزة (لا تظهر في الأعمدة)</span>}
        </div>
      )}
    </div>
  );
}
